// @givmo/employer-module — sign-up DETAILS screen (/sign-up/details). Ports `sign_up_screen.dart`.
//
// "Get started with employer donation matching" + first name / last name / company name / employee
// count. Dual-purpose, discriminated by the EXPLICIT `provider` route search param (the Flutter
// `isProviderAuth`) — NOT by `isAuthenticated`:
//   - EMAIL path (no `provider` flag) → store the details + go to /sign-up/email-password
//     ("Continue with Email"). The details ride a module-level store to the code screen, which
//     registers the company once the work email is proven (NF-FIX21).
//   - PROVIDER path (`?provider=true`, the landing for an account that exists with no company: a
//     provider sign-in/up, or an EMAIL account that already proved its address — sent back here when
//     the registration after its code was refused, or landed here later) → register the company
//     directly (`use-register-company.ts`, "Get started"). The company create refuses an unverified
//     email (NF-FIX21), so an account whose email is not yet verified (an app account with a phone
//     linked, landed here directly) is sent to the code first — `/sign-up/otp?provider=true`, carrying
//     what it typed — and never reaches the create. On success every account goes to the pending
//     room: its email is verified by then, so a second code would prove nothing (the code's only
//     effect is that flag). A refusal carried over from the code screen shows here, over the details
//     it carried, until the next submit.
// D-EP-60 (5): a signed-in visitor, on EITHER path, is asked first whether the account already works
// for a company on Givmo (`employer-of-record.ts`) — and if it does, the screen shows nothing and hands
// it to the request-access screen, which says how to get in. The provider path is where a landing sends
// a signed-in caller; the email path is reached signed in only by its address, a bookmark or a history
// entry, and is asked all the same. A signed-out visitor, the email path's real caller, has no account
// to ask about: the form renders at once, with no read, as before.
// Normal disabled-until-valid buttons (the Flutter always-tappable workaround dropped; the VALIDATION
// LOGIC — letters-only names, int employee count — is preserved in the Zod schema).
//
// Rendered by both hosts (EP-MNT8): the `provider` flag arrives as a prop (each host reads its own
// route's search), and the auth, addresses, look and frame come from `useSignUpHost()`.

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  Form,
  FormField,
  TextField,
  ErrorMessage,
  useGivmoForm,
  testIdProp,
} from '@givmo/design-system';
import { SignUpHeading, useSignUpHost } from './sign-up-host';
import { useAuthError } from './use-auth-error';
import { useSignUpDetails } from './sign-up-context';
import { useRegisterCompany } from './use-register-company';
import { useEmployerOfRecord } from './employer-of-record';
import {
  signUpDetailsSchema,
  INVALID_NAME_MESSAGE,
  INVALID_EMPLOYEE_COUNT_MESSAGE,
  type SignUpDetailsFormValues,
} from './auth-schemas';

export interface SignUpDetailsScreenProps {
  /** The route's `?provider=true` — the provider-registration path (see the header). */
  provider?: boolean;
}

export function SignUpDetailsScreen({ provider }: SignUpDetailsScreenProps) {
  const { auth, paths, look, Shell } = useSignUpHost();
  const { user, signOut } = auth;
  const navigate = useNavigate();
  const { details, registrationError, setDetails, setRegistrationError } = useSignUpDetails(
    user?.uid ?? null,
  );
  const registerCompany = useRegisterCompany();
  const { error, clearError, surface } = useAuthError();
  const [submitting, setSubmitting] = useState(false);

  // EXPLICIT provider signal (the Flutter `isProviderAuth`), carried as a route search param ONLY on
  // the provider/unregistered navigations to this screen. We do NOT infer it from `isAuthenticated`:
  // a back-navigated, already-registered email user is also authenticated and must take the EMAIL
  // path — never re-run the registration (which would duplicate-create the company).
  const isProviderRegistration = provider === true;

  // NF-FIX21: an email sign-up whose registration was refused after its code comes back HERE with
  // what it typed, so only the field at fault needs changing. The email path starts empty, as before.
  const carried = isProviderRegistration ? details : null;
  const form = useGivmoForm(signUpDetailsSchema, {
    defaultValues: {
      firstName: carried?.firstName ?? '',
      lastName: carried?.lastName ?? '',
      companyName: carried?.companyName ?? '',
      numberOfEmployees: carried === null ? '' : String(carried.numberOfEmployees),
    },
    mode: 'onTouched',
  });
  // The refusal the code screen carried over — shown until this screen's own next submit.
  const shownError = error ?? (isProviderRegistration ? registrationError : null);

  const onSubmit = async (values: SignUpDetailsFormValues) => {
    clearError();
    setRegistrationError(null);
    const parsed = {
      firstName: values.firstName,
      lastName: values.lastName,
      companyName: values.companyName.trim(),
      numberOfEmployees: Number(values.numberOfEmployees),
    };

    if (isProviderRegistration) {
      if (user !== null && user.emailVerified !== true) {
        // NF-FIX21: the create would refuse this unverified email ("Permission denied", and nothing
        // here could offer a code). Prove it first; the code screen re-resolves back here, prefilled.
        setDetails(parsed);
        await navigate({ to: paths.signUpOtp, search: { provider: true } });
        return;
      }
      setSubmitting(true);
      try {
        const outcome = await registerCompany(parsed);
        if (outcome === 'created') {
          // A verified account (every one that reaches the create, above) waits in the pending room;
          // a second code would only re-set a flag already set.
          await navigate({
            to: user?.emailVerified === true ? paths.signUpVerified : paths.signUpOtp,
          });
        }
      } catch (thrown) {
        surface(thrown);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Email path: carry the details to the email+password screen.
    setDetails(parsed);
    await navigate({ to: paths.signUpEmailPassword });
  };

  // EP-SUITE2-FIX5 ⚠ BACK OUT OF A PROVIDER REGISTRATION SIGNS OUT FIRST. The chooser is a door for
  // a visitor with no session, and `/sign-up` lands a signed-in, company-less caller on their funnel
  // screen — for a provider registration, THIS one (the console lands them through its root, the same
  // decision) — so a plain navigation would bounce straight back here: a Back that does nothing.
  // Leaving the registration means leaving the account it was started with; the host's sign-out
  // releases any suite session before the local one. The email path (no session yet) navigates
  // exactly as before.
  const onBack = async () => {
    if (isProviderRegistration && user !== null) await signOut();
    await navigate({ to: paths.signUp });
  };

  const nameError = form.formState.errors.firstName ?? form.formState.errors.lastName;

  // D-EP-60 (5): never offer a company's employee to register one, on either path (the header).
  const employer = useEmployerOfRecord(user);
  useEffect(() => {
    if (employer.company !== null) {
      void navigate({ to: paths.signUpRequestAccess, replace: true });
    }
  }, [employer.company, navigate, paths]);

  const back = (
    <button
      type="button"
      onClick={() => void onBack()}
      className={look.textButton}
      {...testIdProp('auth-signup-back')}
    >
      Back
    </button>
  );

  if (!employer.settled || employer.company !== null) return null;

  return (
    <Shell testId="page-sign-up-details" wide>
      {/* Back at the top of the card, where every step keeps it. */}
      {back}
      <SignUpHeading
        heading={<h1 className={look.heading}>Get started with employer donation matching</h1>}
      />

      <Form form={form} onSubmit={onSubmit} testId="auth-signup-details-form">
        <div className="flex gap-16">
          <div className="flex-1">
            <FormField
              name="firstName"
              render={({ field, invalid }) => (
                <TextField
                  label="Your first name"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={invalid}
                  testId="auth-signup-firstname"
                />
              )}
              hideError
            />
          </div>
          <div className="flex-1">
            <FormField
              name="lastName"
              render={({ field, invalid }) => (
                <TextField
                  label="Your last name"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={invalid}
                  testId="auth-signup-lastname"
                />
              )}
              hideError
            />
          </div>
        </div>
        {nameError && (
          <ErrorMessage testId="auth-signup-name-error">{INVALID_NAME_MESSAGE}</ErrorMessage>
        )}

        <div className="flex gap-16">
          <div className="flex-[2]">
            <FormField
              name="companyName"
              render={({ field, invalid }) => (
                <TextField
                  label="Company name"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={invalid}
                  testId="auth-signup-companyname"
                />
              )}
              hideError
            />
          </div>
          <div className="flex-1">
            <FormField
              name="numberOfEmployees"
              render={({ field, invalid }) => (
                <TextField
                  label="Employee count"
                  inputMode="numeric"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={invalid}
                  testId="auth-signup-employeecount"
                />
              )}
              hideError
            />
          </div>
        </div>
        {form.formState.errors.numberOfEmployees && (
          <ErrorMessage testId="auth-signup-employeecount-error">
            {INVALID_EMPLOYEE_COUNT_MESSAGE}
          </ErrorMessage>
        )}

        {shownError !== null && <ErrorMessage testId="auth-error">{shownError}</ErrorMessage>}

        <Button
          type="submit"
          {...look.primary}
          loading={submitting}
          disabled={!form.formState.isValid || submitting}
          testId="auth-signup-continue"
        >
          {isProviderRegistration ? 'Get started' : 'Continue with Email'}
        </Button>
      </Form>
    </Shell>
  );
}
